import React from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { setCurrentStep } from "../../checkoutSlice";
import { selectSelectedPaymentMethod } from "../../checkoutSelectors";
import { useGetCheckoutDataQuery } from "../../api/checkoutApi";
import { useGetCartQuery } from "../../../cart/api/cartApi";
import CheckoutStepLayout from "../common/CheckoutStepLayout";
import OrderReview from "./OrderReview/OrderReview";
import PaymentOptions from "./PaymentOptions";
import PlaceOrderActions from "./PlaceOrderActions";
import CustomerNoteForm from "./CustomerNoteForm";
import { useCheckoutSubmit } from "../../hooks/useCheckoutSubmit";
import DeliveryEstimateDisplay from "../../../product/components/common/DeliveryEstimateDisplay";

const PaymentStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);

  const { data: checkoutData, isLoading: isCheckoutLoading } =
    useGetCheckoutDataQuery();
  const { data: cartData, isLoading: isCartLoading } = useGetCartQuery();

  const { submitOrder, isProcessing } = useCheckoutSubmit();

  const handleNavigation = (step: "billing" | "shipping" | "appointment") => {
    dispatch(setCurrentStep(step));
  };

  const handleBack = () => {
    const hasShipping = cartData?.needsShipping;
    const hasAppointment =
      checkoutData?.appointment?.needsAppointment ||
      cartData?.appointment?.needsAppointment;

    if (hasAppointment) {
      handleNavigation("appointment");
    } else if (hasShipping) {
      handleNavigation("shipping");
    } else {
      handleNavigation("billing");
    }
  };

  const handlePlaceOrder = () => {
    if (!selectedPaymentMethod || !checkoutData) {
      toast.error(t("paymentOptions.noPaymentMethods"));
      return;
    }
    submitOrder(checkoutData, selectedPaymentMethod);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  if (isCheckoutLoading || isCartLoading) {
    return (
      <div className="checkout-step">
        <div className="loader-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <OrderReview
        checkoutData={checkoutData}
        cartData={cartData}
        onEditBilling={() => handleNavigation("billing")}
        onEditShipping={() => handleNavigation("shipping")}
      />

      {cartData?.needsShipping && (
        <DeliveryEstimateDisplay
          estimate={checkoutData?.deliveryEstimation}
          className="checkout-payment-delivery"
        />
      )}

      <CustomerNoteForm />

      <CheckoutStepLayout
        title={t("paymentStep.stepTitle", "Payment")}
        onSubmit={onSubmit}
        isProcessing={false}
        nextButtonText=""
        showNextButton={false}
        showPrevButton={true}
        prevButtonType="button"
        onPrevious={handleBack}
      >
        <PaymentOptions />
      </CheckoutStepLayout>

      <PlaceOrderActions
        onPlaceOrder={handlePlaceOrder}
        disabled={!selectedPaymentMethod}
        isLoading={isProcessing}
        selectedPaymentMethod={selectedPaymentMethod}
      />
    </>
  );
};

export default PaymentStep;
